import styled from 'styled-components';

export const ActivitiesContainer = styled.div`
  background: var(--bg-section);
  min-height: calc(100vh - 68px);
  padding: 40px 20px 72px;

  > * {
    max-width: 1100px;
    margin-left: auto;
    margin-right: auto;
  }
`;

export const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  h1 {
    font-size: var(--text-3xl);
    letter-spacing: -0.5px;
    margin-bottom: 4px;
  }

  p {
    color: var(--text-secondary);
  }
`;

export const CreateButton = styled.button`
  background: var(--primary);
  color: var(--white);
  border: none;
  padding: 12px 28px;
  border-radius: var(--radius-round);
  font-family: var(--font-brand);
  font-size: var(--text-base);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-primary);
  }
`;

export const FilterSection = styled.div`
  margin-bottom: 24px;

  select {
    padding: 10px 14px;
    background: var(--bg-card);
    border: 1.5px solid var(--border-light);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-family: var(--font-brand);
    font-size: var(--text-sm);
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: var(--border-focus);
    }
  }
`;

export const ActivitiesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ActivityCard = styled.article`
  display: grid;
  grid-template-columns: 200px 1fr auto;
  gap: 20px;
  background: ${props => (props.status === 'inactive' || props.status === 'draft') ? 'var(--bg-section)' : 'var(--bg-card)'};
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 18px;
  box-shadow: var(--shadow-xs);
  transition: box-shadow var(--transition-fast);

  &:hover { box-shadow: var(--shadow-sm); }

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

export const ActivityImage = styled.img`
  width: 200px;
  height: 150px;
  object-fit: cover;
  border-radius: var(--radius-md);
  background: var(--bg-input);

  @media (max-width: 820px) {
    width: 100%;
    height: 180px;
  }
`;

export const ActivityInfo = styled.div`
  min-width: 0;
`;

export const ActivityTitle = styled.h3`
  font-size: var(--text-lg);
`;

export const ActivityDescription = styled.p`
  color: var(--text-secondary);
  font-size: var(--text-sm);
  line-height: 1.5;
  margin-top: 6px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const ActivityPrice = styled.span`
  font-size: var(--text-xl);
  font-weight: 700;
  color: var(--secondary);
`;

export const ActivityStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;

  span {
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--text-secondary);
    background: var(--bg-section);
    padding: 4px 10px;
    border-radius: var(--radius-round);
  }
`;

export const ActivityActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;

  button {
    background: var(--bg-card);
    border: 1.5px solid var(--border-medium);
    color: var(--text-primary);
    padding: 8px 16px;
    border-radius: var(--radius-round);
    font-family: var(--font-brand);
    font-size: var(--text-xs);
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: all var(--transition-fast);

    &:hover {
      border-color: var(--primary);
      color: var(--primary);
      background: var(--primary-subtle);
    }

    &.delete:hover {
      border-color: var(--danger);
      color: var(--danger);
      background: var(--danger-subtle);
    }
  }

  @media (max-width: 820px) {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

export const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: var(--bg-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

export const ModalContent = styled.div`
  background: var(--bg-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  padding: 32px;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow-y: auto;

  h2 {
    font-size: var(--text-2xl);
    margin-bottom: 18px;
  }

  h4 {
    font-size: var(--text-base);
    margin: 20px 0 12px;
  }

  @media (max-width: 560px) {
    padding: 20px 16px;
  }
`;

export const TabMenu = styled.div`
  display: flex;
  gap: 6px;
  background: var(--bg-input);
  border-radius: var(--radius-round);
  padding: 5px;
  margin-bottom: 24px;
`;

export const TabButton = styled.button`
  flex: 1;
  padding: 10px 12px;
  border: none;
  border-radius: var(--radius-round);
  background: ${props => (props.active ? 'var(--bg-card)' : 'transparent')};
  color: ${props => (props.active ? 'var(--primary)' : 'var(--text-secondary)')};
  font-family: var(--font-brand);
  font-size: var(--text-sm);
  font-weight: ${props => (props.active ? 700 : 500)};
  box-shadow: ${props => (props.active ? 'var(--shadow-xs)' : 'none')};
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 560px) {
    font-size: var(--text-xs);
    padding: 8px 4px;
    white-space: normal;
    text-overflow: clip;
    line-height: 1.25;
  }
`;

export const AvailabilitySection = styled.div``;

export const TimeSlotGrid = styled.div`
  background: var(--bg-section);
  border-radius: var(--radius-md);
  padding: 16px;
  margin: 14px 0;

  h4 { margin-top: 0; }
`;

export const TimeSlot = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1.4fr auto;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;

  > * { min-width: 0; }

  @media (max-width: 560px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const CapacitySettings = styled.div`
  margin-top: 20px;
`;

export const DaySettings = styled.div`
  margin-top: 20px;
  background: var(--bg-section);
  border-radius: var(--radius-md);
  padding: 16px;

  h4 { margin-top: 0; }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 64px 20px;
  background: var(--bg-card);
  border: 1px dashed var(--border-medium);
  border-radius: var(--radius-lg);

  h3 { margin-bottom: 8px; }

  p {
    color: var(--text-secondary);
    font-size: var(--text-sm);
    margin-bottom: 20px;
  }
`;

/* Rejillas de formulario que colapsan a una columna en teléfono */
export const DayRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-sm) var(--spacing-md);
  margin-bottom: var(--spacing-sm);

  > input { flex: 1 1 160px; width: auto; }
`;

export const DayName = styled.div`
  min-width: 100px;
  font-size: var(--text-sm);

  @media (max-width: 560px) {
    min-width: 84px;
  }
`;
